// Repuestos — inventario con stock (bajo mínimo resaltado), crear/editar y
// registro de movimientos (entrada / salida / ajuste). El 409
// stock_insuficiente de una salida se muestra dentro del dialog con el
// mensaje del backend (incluye disponible vs solicitado).

import { useState } from 'react';
import { toast } from 'sonner';
import type { RepuestoMtto, TipoMovimientoRepuesto } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { cn } from '../../lib/utils';
import { CargandoModulo, ErrorModulo, MantenimientoShell } from './MantenimientoShell';
import {
  useActualizarRepuestoMtto,
  useCrearMovimientoRepuesto,
  useCrearRepuestoMtto,
  useRepuestosMtto,
} from './queries';
import { BannerSinRolMtto, esCodigoApi, fmtNum, mensajeError, usePermisosMtto } from './shared';

function bajoMinimo(r: RepuestoMtto): boolean {
  return r.min_cant !== null && Number(r.existencia) < Number(r.min_cant);
}

export function RepuestosMttoPage() {
  const { puedeOperarInventario } = usePermisosMtto();
  const [busqueda, setBusqueda] = useState('');
  const [soloBajoMinimo, setSoloBajoMinimo] = useState(false);
  const [dialogo, setDialogo] = useState<
    | { modo: 'crear' }
    | { modo: 'editar'; repuesto: RepuestoMtto }
    | { modo: 'movimiento'; repuesto: RepuestoMtto }
    | null
  >(null);

  const { data, isLoading, isError, error } = useRepuestosMtto({
    ...(busqueda.trim() ? { q: busqueda.trim() } : {}),
    ...(soloBajoMinimo ? { bajo_minimo: true } : {}),
  });

  return (
    <MantenimientoShell
      titulo="Repuestos"
      subtitulo={data ? `${data.length} repuesto(s) con los filtros actuales` : undefined}
      acciones={
        puedeOperarInventario ? (
          <Button onClick={() => setDialogo({ modo: 'crear' })}>Crear repuesto</Button>
        ) : undefined
      }
    >
      <Card>
        <CardContent className="flex flex-col gap-3 p-4 pt-4 md:flex-row md:items-end">
          <div className="flex flex-1 flex-col gap-1">
            <Label htmlFor="rep-q">Buscar</Label>
            <Input
              id="rep-q"
              placeholder="Descripción, código o SKU…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
            />
          </div>
          <label className="flex h-10 items-center gap-2 text-sm text-fg-secondary">
            <input
              type="checkbox"
              className="size-4 accent-[var(--color-accent)]"
              checked={soloBajoMinimo}
              onChange={(e) => setSoloBajoMinimo(e.target.checked)}
            />
            Solo bajo mínimo
          </label>
        </CardContent>
      </Card>

      {isLoading ? <CargandoModulo mensaje="Cargando repuestos…" /> : null}
      {isError ? <ErrorModulo error={error} /> : null}

      {data && data.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-fg-tertiary">
            Sin repuestos con esos filtros.
          </CardContent>
        </Card>
      ) : null}

      {data && data.length > 0 ? (
        <Table>
          <THead>
            <TR>
              <TH>Código</TH>
              <TH>Descripción</TH>
              <TH align="right">Existencia</TH>
              <TH align="right">Mínimo</TH>
              <TH align="right">Costo</TH>
              <TH>Ubicación</TH>
              {puedeOperarInventario ? <TH align="right" /> : null}
            </TR>
          </THead>
          <TBody>
            {data.map((r) => {
              const alerta = bajoMinimo(r);
              return (
                <TR key={r.id} className={cn(alerta && 'bg-crit-soft')}>
                  <TD className="font-mono text-xs text-fg-secondary">{r.codigo ?? '—'}</TD>
                  <TD className="text-sm text-fg">
                    {r.descripcion}
                    {r.sku ? (
                      <div className="font-mono text-[11px] text-fg-tertiary">SKU {r.sku}</div>
                    ) : null}
                  </TD>
                  <TD align="right" className="text-sm tabular-nums">
                    <span className={cn(alerta ? 'font-medium text-crit' : 'text-fg')}>
                      {fmtNum(r.existencia, 2)}
                      {r.unidad ? ` ${r.unidad}` : ''}
                    </span>
                    {alerta ? (
                      <Badge tone="crit" className="ml-2">
                        Bajo mínimo
                      </Badge>
                    ) : null}
                  </TD>
                  <TD align="right" className="text-xs tabular-nums text-fg-secondary">
                    {fmtNum(r.min_cant, 2)}
                  </TD>
                  <TD align="right" className="text-xs tabular-nums text-fg-secondary">
                    ${fmtNum(r.costo, 2)}
                  </TD>
                  <TD className="text-xs text-fg-secondary">{r.ubicacion ?? '—'}</TD>
                  {puedeOperarInventario ? (
                    <TD align="right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDialogo({ modo: 'movimiento', repuesto: r })}
                        >
                          Movimiento
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDialogo({ modo: 'editar', repuesto: r })}
                        >
                          Editar
                        </Button>
                      </div>
                    </TD>
                  ) : null}
                </TR>
              );
            })}
          </TBody>
        </Table>
      ) : null}

      {dialogo?.modo === 'crear' ? (
        <RepuestoFormDialog repuesto={null} onClose={() => setDialogo(null)} />
      ) : null}
      {dialogo?.modo === 'editar' ? (
        <RepuestoFormDialog repuesto={dialogo.repuesto} onClose={() => setDialogo(null)} />
      ) : null}
      {dialogo?.modo === 'movimiento' ? (
        <MovimientoDialog repuesto={dialogo.repuesto} onClose={() => setDialogo(null)} />
      ) : null}
    </MantenimientoShell>
  );
}

// =============================================================================
// Crear / editar repuesto
// =============================================================================

function RepuestoFormDialog({
  repuesto,
  onClose,
}: {
  repuesto: RepuestoMtto | null;
  onClose: () => void;
}) {
  const crear = useCrearRepuestoMtto();
  const actualizar = useActualizarRepuestoMtto();
  const [sinRol, setSinRol] = useState(false);

  const [descripcion, setDescripcion] = useState(repuesto?.descripcion ?? '');
  const [codigo, setCodigo] = useState(repuesto?.codigo ?? '');
  const [unidad, setUnidad] = useState(repuesto?.unidad ?? '');
  const [existencia, setExistencia] = useState(repuesto ? repuesto.existencia : '0');
  const [minCant, setMinCant] = useState(repuesto?.min_cant ?? '');
  const [maxCant, setMaxCant] = useState(repuesto?.max_cant ?? '');
  const [costo, setCosto] = useState(repuesto?.costo ?? '0');
  const [ubicacion, setUbicacion] = useState(repuesto?.ubicacion ?? '');
  const [proveedor, setProveedor] = useState(repuesto?.proveedor ?? '');
  const [sku, setSku] = useState(repuesto?.sku ?? '');

  const guardando = crear.isPending || actualizar.isPending;

  const onGuardar = async () => {
    if (!descripcion.trim()) {
      toast.error('Escriba la descripción del repuesto');
      return;
    }
    try {
      if (repuesto) {
        await actualizar.mutateAsync({
          id: repuesto.id,
          patch: {
            descripcion: descripcion.trim(),
            ...(codigo.trim() ? { codigo: codigo.trim() } : {}),
            ...(unidad.trim() ? { unidad: unidad.trim() } : {}),
            ...(minCant !== '' ? { min_cant: Number(minCant) } : {}),
            ...(maxCant !== '' ? { max_cant: Number(maxCant) } : {}),
            ...(costo !== '' ? { costo: Number(costo) } : {}),
            ...(ubicacion.trim() ? { ubicacion: ubicacion.trim() } : {}),
            ...(proveedor.trim() ? { proveedor: proveedor.trim() } : {}),
            ...(sku.trim() ? { sku: sku.trim() } : {}),
          },
        });
        toast.success('Repuesto actualizado');
      } else {
        await crear.mutateAsync({
          descripcion: descripcion.trim(),
          existencia: existencia === '' ? 0 : Number(existencia),
          costo: costo === '' ? 0 : Number(costo),
          ...(codigo.trim() ? { codigo: codigo.trim() } : {}),
          ...(unidad.trim() ? { unidad: unidad.trim() } : {}),
          ...(minCant !== '' ? { min_cant: Number(minCant) } : {}),
          ...(maxCant !== '' ? { max_cant: Number(maxCant) } : {}),
          ...(ubicacion.trim() ? { ubicacion: ubicacion.trim() } : {}),
          ...(proveedor.trim() ? { proveedor: proveedor.trim() } : {}),
          ...(sku.trim() ? { sku: sku.trim() } : {}),
        });
        toast.success('Repuesto creado');
      }
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'rol_mantenimiento_no_autorizado')) {
        setSinRol(true);
        return;
      }
      toast.error(mensajeError(err));
    }
  };

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{repuesto ? 'Editar repuesto' : 'Crear repuesto'}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {sinRol ? <BannerSinRolMtto accion="gestionar el inventario de repuestos" /> : null}
          <div className="flex flex-col gap-1">
            <Label htmlFor="rep-form-desc">Descripción *</Label>
            <Input
              id="rep-form-desc"
              value={descripcion}
              maxLength={300}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="rep-form-codigo">Código</Label>
              <Input
                id="rep-form-codigo"
                value={codigo}
                maxLength={40}
                onChange={(e) => setCodigo(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="rep-form-sku">SKU</Label>
              <Input
                id="rep-form-sku"
                value={sku}
                maxLength={80}
                onChange={(e) => setSku(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="rep-form-unidad">Unidad</Label>
              <Input
                id="rep-form-unidad"
                value={unidad}
                maxLength={30}
                placeholder="Ej. und, kg, m"
                onChange={(e) => setUnidad(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {repuesto === null ? (
              <div className="flex flex-col gap-1">
                <Label htmlFor="rep-form-existencia">Existencia inicial</Label>
                <Input
                  id="rep-form-existencia"
                  type="number"
                  min="0"
                  step="any"
                  value={existencia}
                  onChange={(e) => setExistencia(e.target.value)}
                />
              </div>
            ) : null}
            <div className="flex flex-col gap-1">
              <Label htmlFor="rep-form-min">Mínimo</Label>
              <Input
                id="rep-form-min"
                type="number"
                min="0"
                step="any"
                value={minCant}
                onChange={(e) => setMinCant(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="rep-form-max">Máximo</Label>
              <Input
                id="rep-form-max"
                type="number"
                min="0"
                step="any"
                value={maxCant}
                onChange={(e) => setMaxCant(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="rep-form-costo">Costo unitario</Label>
              <Input
                id="rep-form-costo"
                type="number"
                min="0"
                step="any"
                value={costo}
                onChange={(e) => setCosto(e.target.value)}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="rep-form-ubicacion">Ubicación</Label>
              <Input
                id="rep-form-ubicacion"
                value={ubicacion}
                maxLength={200}
                onChange={(e) => setUbicacion(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="rep-form-proveedor">Proveedor</Label>
              <Input
                id="rep-form-proveedor"
                value={proveedor}
                maxLength={200}
                onChange={(e) => setProveedor(e.target.value)}
              />
            </div>
          </div>
          {repuesto !== null ? (
            <p className="text-[11px] text-fg-tertiary">
              La existencia se ajusta desde “Movimiento” (entrada / salida / ajuste), no editando el
              repuesto — así queda trazabilidad de cada cambio de stock.
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={guardando}
            onClick={() => {
              void onGuardar();
            }}
          >
            {guardando ? 'Guardando…' : repuesto ? 'Guardar cambios' : 'Crear repuesto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Movimiento de stock (entrada / salida / ajuste)
// =============================================================================

const TIPO_MOVIMIENTO_LABEL: Record<TipoMovimientoRepuesto, string> = {
  entrada: 'Entrada (suma al stock)',
  salida: 'Salida (descuenta del stock)',
  ajuste: 'Ajuste (fija la existencia)',
};

function MovimientoDialog({ repuesto, onClose }: { repuesto: RepuestoMtto; onClose: () => void }) {
  const crearMovimiento = useCrearMovimientoRepuesto();
  const [tipo, setTipo] = useState<TipoMovimientoRepuesto>('entrada');
  const [cantidad, setCantidad] = useState('');
  const [notas, setNotas] = useState('');
  const [errorStock, setErrorStock] = useState<string | null>(null);
  const [sinRol, setSinRol] = useState(false);

  const onRegistrar = async () => {
    const cant = Number(cantidad);
    if (!Number.isFinite(cant) || cant <= 0) {
      toast.error('La cantidad debe ser un número mayor a cero');
      return;
    }
    setErrorStock(null);
    try {
      await crearMovimiento.mutateAsync({
        repuestoId: repuesto.id,
        input: { tipo, cantidad: cant, ...(notas.trim() ? { notas: notas.trim() } : {}) },
      });
      toast.success('Movimiento registrado');
      onClose();
    } catch (err) {
      if (esCodigoApi(err, 'stock_insuficiente')) {
        setErrorStock(mensajeError(err));
        return;
      }
      if (esCodigoApi(err, 'rol_mantenimiento_no_autorizado')) {
        setSinRol(true);
        return;
      }
      toast.error(mensajeError(err));
    }
  };

  return (
    <Dialog open onClose={onClose} className="max-w-md">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar movimiento</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          {sinRol ? <BannerSinRolMtto accion="registrar movimientos de inventario" /> : null}
          <p className="text-sm text-fg-secondary">
            {repuesto.descripcion} · existencia actual{' '}
            <span className="font-medium tabular-nums text-fg">
              {fmtNum(repuesto.existencia, 2)}
              {repuesto.unidad ? ` ${repuesto.unidad}` : ''}
            </span>
          </p>
          {errorStock ? (
            <div className="rounded border border-crit/30 bg-crit-soft px-3 py-2.5 text-xs">
              <p className="font-medium text-crit">Existencia insuficiente</p>
              <p className="mt-0.5 text-fg-secondary">{errorStock}</p>
            </div>
          ) : null}
          <div className="flex flex-col gap-1">
            <Label htmlFor="mov-tipo">Tipo *</Label>
            <Select
              id="mov-tipo"
              value={tipo}
              onChange={(e) => {
                setTipo(e.target.value as TipoMovimientoRepuesto);
                setErrorStock(null);
              }}
            >
              {(Object.keys(TIPO_MOVIMIENTO_LABEL) as TipoMovimientoRepuesto[]).map((t) => (
                <option key={t} value={t}>
                  {TIPO_MOVIMIENTO_LABEL[t]}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="mov-cantidad">
              {tipo === 'ajuste' ? 'Existencia final *' : 'Cantidad *'}
            </Label>
            <Input
              id="mov-cantidad"
              type="number"
              min="0.01"
              step="any"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="mov-notas">Notas</Label>
            <Textarea
              id="mov-notas"
              rows={2}
              maxLength={500}
              placeholder="Motivo del movimiento…"
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={crearMovimiento.isPending}
            onClick={() => {
              void onRegistrar();
            }}
          >
            {crearMovimiento.isPending ? 'Registrando…' : 'Registrar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
